import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CircleParking, Search, Ticket as TicketIcon, IdCard, Wallet, Bell } from "lucide-react";
import { PageHeader, EmptyState } from "../../../components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { useRoleModules } from "../../../lib/roleModules";
import { useLive } from "../../../lib/useLive";
import { api, fmtINR } from "../../../lib/api";
import { ParkingLiveBadge } from "../../../components/parking/common";
import { ParkingPayDialog } from "../../../components/parking/PayFlow";
import { TicketDialog } from "../../../components/parking/Ticket";
import { FindTab } from "./FindTab";
import { BookingsTab } from "./BookingsTab";
import { PassesTab } from "./PassesTab";
import { WalletTab } from "./WalletTab";
import { AlertsTab } from "./AlertsTab";

/* Phase 201 — Citizen portal → Smart Parking (mobile-first). Find a slot on the live map / list, book & pay, show the QR
   ticket at the gate, extend / cancel, buy & renew passes, wallet, receipts, waitlist and parking alerts.
   Everything refreshes every ~10 s through `useLive(code, "citizen")`. */

const TABS = [
  { value: "find", label: "Find & Book", icon: Search },
  { value: "bookings", label: "My Bookings", icon: TicketIcon, badge: (s) => s?.active_bookings?.length },
  { value: "passes", label: "Passes", icon: IdCard, badge: (s) => s?.active_passes },
  { value: "wallet", label: "Wallet & Receipts", icon: Wallet },
  { value: "alerts", label: "Alerts", icon: Bell, badge: (s) => s?.unread_alerts },
];

export default function CitizenParking() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "find";
  const setTab = useCallback((v) => { const p = new URLSearchParams(params); p.set("tab", v); setParams(p, { replace: true }); }, [params, setParams]);
  const access = useRoleModules(code);
  const live = useLive(code, "citizen");
  const [summary, setSummary] = useState(null);
  const [pay, setPay] = useState(null);          // { order, title, lines, onPaid }
  const [ticket, setTicket] = useState(null);    // { booking } | { pass }
  const [bump, setBump] = useState(0);           // local refresh counter (after this tab's own writes)

  const reloadSummary = useCallback(() => api.get(`/t/${code}/parking/citizen/summary`).then((r) => setSummary(r.data)).catch(() => {}), [code]);
  useEffect(() => { reloadSummary(); }, [reloadSummary, live, bump]);
  const refresh = useCallback(() => setBump((b) => b + 1), []);

  // Phase 203 — while a booking ticket is on screen (citizen showing the QR at the gate), follow the live counter so the
  // status flips to "active" the moment the marshal checks them in (and to "completed" on exit) without closing it.
  const ticketBookingId = ticket?.booking?.id;
  useEffect(() => {
    if (!ticketBookingId) return undefined;
    let stale = false;
    api.get(`/t/${code}/parking/citizen/bookings/${ticketBookingId}`)
      .then((r) => { if (!stale) setTicket((t) => (t?.booking?.id === ticketBookingId ? { ...t, booking: { ...t.booking, ...r.data } } : t)); })
      .catch(() => {});
    return () => { stale = true; };
  }, [code, ticketBookingId, live, bump]);

  // shared helpers handed to the tabs
  const ctx = useMemo(() => ({
    code, live: `${live}:${bump}`, summary, refresh,
    startPay: (order, opts = {}) => setPay({ order, ...opts }),
    showTicket: (t) => setTicket(t),
    walletBalance: summary?.wallet_balance ?? 0,
  }), [code, live, bump, summary, refresh]);

  if (access.tenantModulesLoaded && !access.tenantModule("smart_parking")) {
    return (
      <div data-testid="citizen-parking-inactive">
        <PageHeader title="Smart Parking" subtitle="App-based parking" />
        <EmptyState title="Smart Parking is not available in this municipality yet" subtitle="Once the municipality activates the service you will be able to find, book and pay for parking here." />
      </div>
    );
  }

  return (
    <div data-testid="citizen-parking-page" className="space-y-4">
      <PageHeader title={<span className="flex items-center gap-2"><CircleParking className="h-6 w-6 text-primary" /> Smart Parking</span>}
        subtitle="Find a free slot, book & pay in a few taps, show the QR at the gate">
        <ParkingLiveBadge />
      </PageHeader>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="citizen-parking-summary">
          <SummaryChip label="Active bookings" value={summary.active_bookings?.length || 0} onClick={() => setTab("bookings")} testId="summary-active-bookings" />
          <SummaryChip label="Wallet balance" value={fmtINR(summary.wallet_balance)} onClick={() => setTab("wallet")} testId="summary-wallet" />
          <SummaryChip label="Active passes" value={summary.active_passes || 0} onClick={() => setTab("passes")} testId="summary-passes" />
          <SummaryChip label="Waitlist" value={summary.waitlist?.length || 0} sub={summary.waitlist?.some((w) => w.status === "notified") ? "Slot free — book now!" : null} onClick={() => setTab("find")} testId="summary-waitlist" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-4 px-4 overflow-x-auto">
          <TabsList className="inline-flex w-max min-w-full sm:w-auto sm:min-w-0 h-auto" data-testid="citizen-parking-tabs">
            {TABS.map((t) => {
              const n = t.badge ? t.badge(summary) : 0;
              return (
                <TabsTrigger key={t.value} value={t.value} className="min-h-[40px]" data-testid={`citizen-parking-tab-${t.value}`}>
                  <t.icon className="h-4 w-4 mr-1.5" /> {t.label}
                  {n > 0 && <Badge className="ml-1.5 h-5 min-w-5 px-1.5 text-[10px]" data-testid={`citizen-parking-tab-${t.value}-badge`}>{n}</Badge>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
        <TabsContent value="find"><FindTab ctx={ctx} /></TabsContent>
        <TabsContent value="bookings"><BookingsTab ctx={ctx} /></TabsContent>
        <TabsContent value="passes"><PassesTab ctx={ctx} /></TabsContent>
        <TabsContent value="wallet"><WalletTab ctx={ctx} /></TabsContent>
        <TabsContent value="alerts"><AlertsTab ctx={ctx} /></TabsContent>
      </Tabs>

      <ParkingPayDialog code={code} pay={pay} onClose={() => setPay(null)} />
      <TicketDialog open={!!ticket} onClose={() => setTicket(null)} booking={ticket?.booking} pass={ticket?.pass} title={ticket?.title} description={ticket?.description} />
    </div>
  );
}

const SummaryChip = ({ label, value, sub, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="rounded-xl border bg-card px-3 py-2.5 text-left shadow-sm hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold tabular-nums font-heading leading-tight">{value}</p>
    {sub && <p className="text-[11px] text-[hsl(var(--success))] font-medium">{sub}</p>}
  </button>
);
